import type { EngagementRequirement } from '@/lib/api/engagement-requirements';
import type { Suggestions } from '@/lib/api/discovery-suggestions';
import type { AttentionItem, DiscoverySummary } from '@/lib/api/discovery';
import {
  addedFromDiscovery,
  citationOf,
} from '@/components/engagement-requirements/requirements-logic';

/**
 * Pure helpers behind the Discovery and Selection status cards on the
 * engagement Overview. They only arrange what the API returned: the
 * requirement rows, the suggestion groups and the Discovery summary. The
 * Discovery numbers (coverage, sessions, evidence awaiting review) are the
 * server's KPIs and pass through untouched; the only counting here is over
 * the requirement rows the Selection screen already holds in full.
 */

export interface RequirementSourceCounts {
  /** Active requirements (removed ones are not counted anywhere). */
  total: number;
  library: number;
  imported: number;
  manual: number;
  discovery: number;
  critical: number;
  desired: number;
  notSet: number;
}

/**
 * Active requirements by where each came from. A requirement added from a
 * discovery suggestion counts as Discovery whether it landed as a library
 * match or as a client-specific need, so the four buckets sum to the total.
 */
export function requirementSourceCounts(
  rows: ReadonlyArray<
    Pick<EngagementRequirement, 'citation' | 'number' | 'load' | 'status' | 'criticality'>
  >,
): RequirementSourceCounts {
  const out: RequirementSourceCounts = {
    total: 0,
    library: 0,
    imported: 0,
    manual: 0,
    discovery: 0,
    critical: 0,
    desired: 0,
    notSet: 0,
  };
  for (const row of rows) {
    if (row.status === 'removed') continue;
    out.total += 1;
    if (row.criticality === 'critical') out.critical += 1;
    if (row.criticality === 'desired') out.desired += 1;
    if (row.criticality === 'not_set') out.notSet += 1;
    if (addedFromDiscovery(row)) {
      out.discovery += 1;
      continue;
    }
    const kind = citationOf(row).kind;
    if (kind === 'library') out.library += 1;
    else if (kind === 'spreadsheet') out.imported += 1;
    else if (kind === 'manual') out.manual += 1;
  }
  return out;
}

/** Suggestions still waiting for a decision: the library matches and the new ones. */
export function openSuggestionCount(s: Pick<Suggestions, 'groups'> | undefined): number | null {
  if (!s) return null;
  return s.groups.library.length + s.groups.new.length;
}

/** The sign-off attention card of the Discovery summary, if the API sent one. */
export function signoffAttention(s: Pick<DiscoverySummary, 'attention'>): AttentionItem | null {
  return s.attention.find((a) => a.kind === 'signoff') ?? null;
}

/** "3 of 8 covered", the first figure of the coverage line. */
export function coverageText(k: DiscoverySummary['kpis']): string {
  return `${k.corroborated.toLocaleString('en-US')} of ${k.areas.toLocaleString('en-US')} covered`;
}

/** "2 need more sources · 3 no evidence yet". */
export function coverageMetaText(k: DiscoverySummary['kpis']): string {
  return `${k.partial.toLocaleString('en-US')} need more sources · ${k.none.toLocaleString('en-US')} no evidence yet`;
}

/** "4 of 9", the sessions figure. */
export function sessionsText(k: DiscoverySummary['kpis']): string {
  return `${k.interviews_held.toLocaleString('en-US')} of ${k.interviews_total.toLocaleString('en-US')}`;
}
